import React, { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import "../components/css/UploadStandardMAEnquiry.css";
import Select from "react-select";

axios.defaults.withCredentials = true;

const AdminUploadStandardMAEnquiry = () => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const fileInputRef = useRef(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState({});
  const [customLocationRows, setCustomLocationRows] = useState({});

  const [popupAlert, setPopupAlert] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    items: [],
    showUploadAgain: false,
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const closePopupAlert = () => {
    setPopupAlert({
      open: false,
      type: "error",
      title: "",
      message: "",
      items: [],
      showUploadAgain: false,
    });
  };

  const handleUploadAgain = () => {
    window.location.reload();
  };

  const resetPreviewState = () => {
    setResult(null);
    setRows([]);
    setShowHistory(false);
    setPreviewMode(false);
    setCustomerAddresses([]);
    setSelectedLocations({});
    setCustomLocationRows({});
  };

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(
        "/admin/standard-ma-enquiry/customers",
        {
          withCredentials: true,
        },
      );

      setCustomers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("fetchCustomers error:", err);
      setCustomers([]);
    }
  };

  const resetSelectedFile = () => {
    setFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setError("");
    resetPreviewState();
  };

  const formatIssueText = (item) => {
    const contractText = item.contract_no
      ? ` | Contract Number: ${item.contract_no}`
      : "";

    const productText = item.product ? ` | Product: ${item.product}` : "";

    const serialText = item.serial ? ` | Serial: ${item.serial}` : "";

    const existingText = item.existingProducts?.length
      ? ` | Existing Products: ${item.existingProducts.join(", ")}`
      : "";

    return `Row ${item.row || "-"}: ${
      item.reason || ""
    }${contractText}${productText}${serialText}${existingText}`;
  };

  const handleUpload = async (importAction = "preview") => {
    if (!selectedCustomerId) {
      setPopupAlert({
        open: true,
        type: "error",
        title: "กรุณาเลือกลูกค้า",
        message: "กรุณาเลือกลูกค้าก่อนอัปโหลดไฟล์ Excel",
        items: [],
      });
      return;
    }

    if (!file) {
      setPopupAlert({
        open: true,
        type: "error",
        title: "กรุณาเลือกไฟล์",
        message: "กรุณาเลือกไฟล์ Excel ก่อนอัปโหลด",
        items: [],
      });
      return;
    }

    try {
      setLoading(true);
      setError("");

      if (importAction === "preview") {
        resetPreviewState();
      }

      const formData = new FormData();
      formData.append("customer_id", selectedCustomerId);
      formData.append("excelFile", file);
      formData.append("importAction", importAction);
      formData.append("locationMap", JSON.stringify(selectedLocations));

      const response = await axios.post(
        "/admin/standard-ma-enquiry/import",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        },
      );

      const responseData = response.data || null;

      setResult(responseData);
      setRows(
        Array.isArray(responseData?.uploadedRows)
          ? responseData.uploadedRows
          : [],
      );

      if (responseData?.previewOnly) {
        const addresses = Array.isArray(responseData.addresses)
          ? responseData.addresses
          : [];

        const firstAddress = addresses[0]?.address || "";
        const defaultLocations = {};

        responseData.uploadedRows?.forEach((row) => {
          defaultLocations[row.excel_row] = row.location || firstAddress;
        });

        setCustomerAddresses(addresses);
        setSelectedLocations(defaultLocations);
        setCustomLocationRows({});
        setPreviewMode(true);
        setShowHistory(true);

        const warningItems = Array.isArray(responseData?.warnings)
          ? responseData.warnings.map(formatIssueText)
          : [];

        if (warningItems.length > 0) {
          setPopupAlert({
            open: true,
            type: "success",
            title: "ตรวจสอบไฟล์สำเร็จ แต่มีคำเตือน",
            message: `พบคำเตือน ${warningItems.length} รายการ แต่ยังสามารถนำเข้าได้`,
            items: warningItems,
            showUploadAgain: false,
          });
        }

        return;
      }

      setPreviewMode(false);
      setShowHistory(true);
      resetSelectedFile();

      const warningItems = Array.isArray(responseData?.warnings)
        ? responseData.warnings.map(formatIssueText)
        : [];

      setPopupAlert({
        open: true,
        type: "success",
        title:
          warningItems.length > 0
            ? "นำเข้าสำเร็จ แต่มีคำเตือน"
            : "นำเข้าข้อมูลสำเร็จ",
        message:
          warningItems.length > 0
            ? `นำเข้าสำเร็จ ${
                responseData?.inserted || 0
              } รายการ แต่มีคำเตือน ${warningItems.length} รายการ`
            : `ระบบนำเข้าข้อมูลเรียบร้อยแล้ว จำนวน ${
                responseData?.inserted || 0
              } รายการ`,
        items: warningItems,
        showUploadAgain: true,
      });
    } catch (err) {
      const data = err.response?.data;

      if (data?.errors?.length > 0 || data?.warnings?.length > 0) {
        const errorItems = Array.isArray(data.errors)
          ? data.errors.map(formatIssueText)
          : [];

        const warningItems = Array.isArray(data.warnings)
          ? data.warnings.map(formatIssueText)
          : [];

        setResult({
          message: data.error || "พบข้อมูลไม่ถูกต้อง",
          totalRows: data.totalRows || 0,
          inserted: data.inserted || 0,
          updated: data.updated || 0,
          skipped: data.skipped || data.errors?.length || 0,
          errors: data.errors || [],
          warnings: data.warnings || [],
        });

        setRows(Array.isArray(data.uploadedRows) ? data.uploadedRows : []);

        setPopupAlert({
          open: true,
          type: "error",
          title: "ข้อมูลในไฟล์ไม่ถูกต้อง",
          message:
            data.error ||
            "โปรดตรวจสอบข้อมูลในไฟล์ Excel อีกครั้ง ระบบจะแจ้งปัญหาทั้งหมดในครั้งเดียว",
          items: [...errorItems, ...warningItems],
        });

        return;
      }

      setPopupAlert({
        open: true,
        type: "error",
        title: "Upload ไม่สำเร็จ",
        message:
          data?.error || data?.detail || err.message || "Upload ไม่สำเร็จ",
        items: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleDateString("en-GB");
  };

  const handleLocationSelect = (rowKey, value) => {
    if (value === "__custom__") {
      setCustomLocationRows((prev) => ({
        ...prev,
        [rowKey]: true,
      }));

      setSelectedLocations((prev) => ({
        ...prev,
        [rowKey]: "",
      }));

      return;
    }

    setCustomLocationRows((prev) => ({
      ...prev,
      [rowKey]: false,
    }));

    setSelectedLocations((prev) => ({
      ...prev,
      [rowKey]: value,
    }));
  };

  const handleConfirmUpload = () => {
    const hasEmptyLocation = rows.some((row) => {
      const location = selectedLocations[row.excel_row];
      return !location || !String(location).trim();
    });

    if (hasEmptyLocation) {
      setPopupAlert({
        open: true,
        type: "error",
        title: "กรุณาระบุ Location",
        message: "กรุณาระบุ Location ให้ครบทุกแถวก่อนนำเข้าข้อมูล",
        items: [],
      });
      return;
    }

    handleUpload("commit");
  };

  const customerOptions = useMemo(() => {
    if (!Array.isArray(customers)) {
      return [];
    }

    return customers
      .filter((item) => item?.id)
      .map((item) => ({
        value: String(item.id),
        label: `${item.company_name || "-"} (${item.name || "-"})`,
      }));
  }, [customers]);

  return (
    <div>
      <div className="agent-container">
        <div className="sma-header">
          <h2 className="sma-title">Upload MA Enquiry</h2>
        </div>

        <div className="sma-template-box">
          <div>
            <strong>Excel Template</strong>
          </div>

          <a
            href="/templates/standard-ma-enquiry-template.xlsx"
            download="standard-ma-enquiry-template.xlsx"
            className="sma-template-download-btn"
          >
            ดาวน์โหลด Excel Template
          </a>
          <a
            href="/templates/Client_MA_Upload_Manual.pdf"
            download="Client_MA_Upload_Manual.pdf"
            className="sma-template-download-btn"
          >
            คู่มือการใช้งานอัปโหลด Template
          </a>
        </div>
        <br />
        <div className="form-group" style={{ marginBottom: "16px" }}>
          <label>Select Customer:</label>

          <Select
            options={customerOptions}
            value={
              customerOptions.find(
                (option) => option.value === String(selectedCustomerId),
              ) || null
            }
            onChange={(selectedOption) => {
              setSelectedCustomerId(selectedOption?.value || "");
              setError("");
              resetPreviewState();
              resetSelectedFile();
            }}
            placeholder="ค้นหาชื่อลูกค้าหรือบริษัท"
            noOptionsMessage={() => "ไม่พบข้อมูล"}
            isSearchable
            isClearable
            classNamePrefix="sma-customer"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </div>

        <div className="sma-upload-box">
          <input
            ref={fileInputRef}
            className="sma-file-input"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
          />

          <button
            className="sma-upload-btn"
            onClick={() => handleUpload("preview")}
            disabled={loading}
          >
            {loading ? "Checking..." : "ตรวจสอบไฟล์ Excel"}
          </button>
        </div>

        {error && <div className="sma-alert sma-alert-error">{error}</div>}

        {result && (
          <div className="sma-result-box">
            <p>
              <strong>{result.message}</strong>
            </p>

            <p>Total Rows: {result.totalRows}</p>
            <p>Inserted: {result.inserted}</p>
            <p>Skipped: {result.skipped}</p>

            {result.errors?.length > 0 && (
              <div className="sma-error-list">
                <h4>Errors</h4>
                <ul>
                  {result.errors.map((item, index) => (
                    <li key={index}>{formatIssueText(item)}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.warnings?.length > 0 && (
              <div className="sma-error-list">
                <h4>Warnings</h4>
                <ul>
                  {result.warnings.map((item, index) => (
                    <li key={index}>{formatIssueText(item)}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {showHistory && (
          <div className="sma-table-wrap">
            <div className="sma-history-header">
              <h3 className="sma-history-title">Uploaded This Time</h3>
            </div>

            <table className="sma-table">
              <thead>
                <tr>
                  <th>Contract Number</th>
                  <th>Category</th>
                  <th>Product</th>
                  <th>Serial</th>
                  <th>Current MA Type</th>
                  <th>Current SLA</th>
                  <th>Current End Date</th>
                  <th className="sma-location-sticky-col">Location</th>
                </tr>
              </thead>

              <tbody>
                {rows.length > 0 ? (
                  rows.map((item, index) => (
                    <tr key={item.id || index}>
                      <td>{item.contract_no || "-"}</td>
                      <td>{item.type || "-"}</td>
                      <td>{item.product || "-"}</td>
                      <td>{item.serial || "-"}</td>
                      <td>{item.current_ma_type || "-"}</td>
                      <td>{item.current_sla || "-"}</td>
                      <td>{formatDate(item.current_end_date)}</td>

                      <td className="sma-location-sticky-col">
                        {previewMode ? (
                          <div className="sma-location-cell">
                            <select
                              className="sma-location-select"
                              value={
                                customLocationRows[item.excel_row]
                                  ? "__custom__"
                                  : selectedLocations[item.excel_row] || ""
                              }
                              onChange={(e) =>
                                handleLocationSelect(
                                  item.excel_row,
                                  e.target.value,
                                )
                              }
                            >
                              {customerAddresses.length > 0 ? (
                                customerAddresses.map((address) => (
                                  <option
                                    key={address.id}
                                    value={address.address}
                                  >
                                    {address.address}
                                  </option>
                                ))
                              ) : (
                                <option value="">ไม่พบที่อยู่เดิม</option>
                              )}

                              <option value="__custom__">
                                กรอกที่อยู่ใหม่
                              </option>
                            </select>

                            {customLocationRows[item.excel_row] && (
                              <textarea
                                className="sma-location-input"
                                value={selectedLocations[item.excel_row] || ""}
                                onChange={(e) =>
                                  setSelectedLocations((prev) => ({
                                    ...prev,
                                    [item.excel_row]: e.target.value,
                                  }))
                                }
                                placeholder="กรุณากรอกที่อยู่ใหม่..."
                              />
                            )}
                          </div>
                        ) : (
                          item.location || "-"
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="sma-empty">
                      ไม่พบข้อมูล
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {previewMode && (
              <div className="sma-preview-actions">
                <p className="sma-preview-note">
                  กรุณาตรวจสอบข้อมูลและระบุ Location ให้ถูกต้องก่อนนำเข้าข้อมูล
                </p>

                <button
                  type="button"
                  className="sma-upload-btn"
                  onClick={handleConfirmUpload}
                  disabled={loading}
                >
                  {loading ? "Uploading..." : "ยืนยัน Upload เข้าระบบ"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {popupAlert.open && (
        <div className="sma-popup-overlay">
          <div className="sma-popup-card">
            <div
              className={
                popupAlert.type === "success"
                  ? "sma-popup-icon sma-popup-success-icon"
                  : "sma-popup-icon"
              }
            >
              {popupAlert.type === "success" ? "✓" : "!"}
            </div>

            <h3 className="sma-popup-title">{popupAlert.title}</h3>

            <p className="sma-popup-text">{popupAlert.message}</p>

            {popupAlert.items.length > 0 && (
              <div className="sma-popup-serial-box">
                {popupAlert.items.map((item, index) => (
                  <span key={index} className="sma-popup-serial">
                    {item}
                  </span>
                ))}
              </div>
            )}

            <div className="sma-popup-actions">
              <button
                type="button"
                className={
                  popupAlert.type === "success"
                    ? "sma-popup-btn sma-popup-success"
                    : "sma-popup-btn sma-popup-cancel"
                }
                onClick={closePopupAlert}
              >
                ตกลง
              </button>

              {popupAlert.showUploadAgain && (
                <button
                  type="button"
                  className="sma-popup-btn sma-popup-upload-again"
                  onClick={handleUploadAgain}
                >
                  อัปโหลดอีกครั้ง
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUploadStandardMAEnquiry;
